/**
 * WEB-1078 п.3 — DOM-обвязка адаптивной вуали фото-фона: грузит картинку,
 * читает её реальные пиксели через canvas в НЕСКОЛЬКИХ точках (под разными
 * частями экрана фотография разной яркости) и переводит их в силу вуали
 * через чистый счёт из themes/adaptiveScrim.ts.
 *
 * Пока картинка не просэмплирована — держит ADAPTIVE_SCRIM_FALLBACK_OPACITY:
 * силу, которой хватает под ЛЮБОЙ фотографией (см. adaptiveScrim.ts), поэтому
 * текст никогда не оказывается незащищённым — ни на первом рендере, ни при
 * ошибке загрузки/CORS-запрете на чтение канваса.
 */
import { useEffect, useState } from 'react';
import {
  ADAPTIVE_SCRIM_FALLBACK_OPACITY,
  requiredScrimOpacityForSamples,
  type SampleRgb,
} from '@/themes/adaptiveScrim';

/**
 * Относительные (0..1) точки сэмплирования — верх/центр/низ, оба края и все
 * ЧЕТЫРЕ УГЛА, поэтому под текстом в ЛЮБОЙ раскладке экрана (alignY/alignX
 * top/center/bottom, left/center/right) окажется хотя бы одна сэмплированная
 * точка.
 *
 * Дефект PM 10.08 («ночное окно»): прежний набор был крестом из 5 точек —
 * верх/центр/низ строго по центральной ВЕРТИКАЛИ и оба края строго по
 * центральной ГОРИЗОНТАЛИ. Ни одна из них не задевает угол кадра. Типичная
 * композиция «окно/луна ночью» кладёт яркий источник света именно в угол, а
 * не по центру — крест его не видел, requiredScrimOpacityForSamples честно
 * получал только тёмные сэмплы и решал «вуаль не нужна», хотя угол кадра
 * оставался ярким и незащищённым. Там, где canvas-сэмплирование успевает
 * отработать (см. useAdaptivePhotoScrim.test.ts «дефект PM 10.08»), это и
 * читалось как «текст сливается с фоном» — вуаль получалась заметно слабее,
 * чем на самом деле нужно фотографии. Добавленные 4 угла закрывают этот
 * пробел; существующие 5 точек не убраны — они по-прежнему покрывают
 * центральные раскладки текста.
 */
const SAMPLE_POINTS: ReadonlyArray<readonly [number, number]> = [
  [0.5, 0.15],
  [0.5, 0.5],
  [0.5, 0.85],
  [0.15, 0.5],
  [0.85, 0.5],
  [0.15, 0.15],
  [0.85, 0.15],
  [0.15, 0.85],
  [0.85, 0.85],
];

/** Картинка уменьшается до этого размера перед чтением — сэмплу нужна средняя яркость области, не полное разрешение фото. */
const SAMPLE_CANVAS_SIZE = 32;

function sampleImage(img: HTMLImageElement): SampleRgb[] | null {
  const canvas = document.createElement('canvas');
  canvas.width = SAMPLE_CANVAS_SIZE;
  canvas.height = SAMPLE_CANVAS_SIZE;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.drawImage(img, 0, 0, SAMPLE_CANVAS_SIZE, SAMPLE_CANVAS_SIZE);
  return SAMPLE_POINTS.map(([u, v]) => {
    const x = Math.min(SAMPLE_CANVAS_SIZE - 1, Math.floor(u * SAMPLE_CANVAS_SIZE));
    const y = Math.min(SAMPLE_CANVAS_SIZE - 1, Math.floor(v * SAMPLE_CANVAS_SIZE));
    const [r, g, b] = ctx.getImageData(x, y, 1, 1).data;
    return [r, g, b] as SampleRgb;
  });
}

/**
 * Непрозрачность адаптивной вуали для фото-фона с отключённым overlay.
 *
 * @param imageUrl - фон, который нужно просэмплировать; undefined — сэмпл не запускается.
 * @param active - точка монтирования сейчас реально рисует этот фон (см.
 *   appliesTo в ScreenBackgroundWrapper) — иначе сэмплирование лишнее.
 */
export function useAdaptivePhotoScrim(imageUrl: string | undefined, active: boolean): number {
  const [opacity, setOpacity] = useState<number>(ADAPTIVE_SCRIM_FALLBACK_OPACITY);

  useEffect(() => {
    if (!active || !imageUrl || typeof window === 'undefined') return;

    let cancelled = false;
    setOpacity(ADAPTIVE_SCRIM_FALLBACK_OPACITY);

    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      if (cancelled) return;
      try {
        const samples = sampleImage(img);
        if (samples) setOpacity(requiredScrimOpacityForSamples(samples));
      } catch {
        // CORS-tainted canvas или иная ошибка чтения — держим безопасный
        // фолбэк (он и так достаточен под любой фотографией), рендер не рвём.
      }
    };
    // Ошибка сети/декодирования оставляет фолбэк как есть — onerror нарочно
    // без обработчика: состояние уже безопасное, дублировать нечего.
    img.src = imageUrl;

    return () => {
      cancelled = true;
    };
  }, [imageUrl, active]);

  return opacity;
}
